import { useCallback, useEffect, useMemo } from "react";
import { Admin, CustomRoutes } from "react-admin";
import { Route } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useBaseRoute, useRouteId } from "@/utils/route-utility";
import { checkAndClearCacheFromURL } from "@/utils/clear-permission-cache";
import { useTenantBrandedThemes } from "@/hooks/useTenantBrandedThemes";

import withClientVersionCheck from "@/components/ClientVersionCheck";
import { TenantBrandingProvider } from "@/context/TenantBrandingProvider";
import dataProvider from "@/dataprovider";
import authProvider from "@/auth-provider";
import layout from "@/layout";
import DashboardAdmin from "@/layout/DashboardAdmin";
import Login from "@/layout/Login";
import MovedNotice from "@/layout/MovedNotice";
import { buildAdminResourceChildren } from "@/components/ResourcesRenderer";
import { AUTHLESS_ROUTES } from "@/config/authless-routes";
import { stableRouterProvider } from "@/utils/stableRouterProvider";
import FmbDistributorPortalPage from "@/containers/fmb/fmb-distributor-portal/FmbDistributorPortalPage";
import TenantSelectorPage from "@/containers/super-admin/TenantSelectorPage";
import i18nProvider from "@/config/i18n";
import { MOVED_NOTICE_PATH } from "@/utils/tenant-retirement";
import type { PermissionRecord } from "@/types/permissions";

dayjs.extend(utc);

const MainApp = () => {
  const baseRoute = useBaseRoute();
  const routeId = useRouteId();
  const themes = useTenantBrandedThemes();

  useEffect(() => {
    checkAndClearCacheFromURL();
  }, []);

  // Stable callback + direct Resource nodes (not a wrapper component — see buildAdminResourceChildren).
  const renderResources = useCallback(
    (permissions: PermissionRecord) => buildAdminResourceChildren(permissions, baseRoute, routeId),
    [baseRoute, routeId]
  );

  const authlessCustomRoutes = useMemo(
    () => (
      <CustomRoutes noLayout>
        {AUTHLESS_ROUTES.map(({ path, element: Element }) => (
          <Route key={path} path={path} element={<Element />} />
        ))}
      </CustomRoutes>
    ),
    []
  );

  const distributorPortalRoute = useMemo(
    () => (
      <CustomRoutes noLayout key="fmb-distributor-portal-routes">
        <Route path="/fmb-distributor-portal" element={<FmbDistributorPortalPage />} />
      </CustomRoutes>
    ),
    []
  );

  const superAdminRoutes = useMemo(
    () => (
      <CustomRoutes noLayout key="super-admin-routes">
        <Route path="/select-tenant" element={<TenantSelectorPage />} />
      </CustomRoutes>
    ),
    []
  );

  const movedNoticeRoute = useMemo(
    () => (
      <CustomRoutes noLayout key="moved-notice-routes">
        <Route path={MOVED_NOTICE_PATH} element={<MovedNotice />} />
      </CustomRoutes>
    ),
    []
  );

  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      i18nProvider={i18nProvider}
      layout={layout}
      dashboard={DashboardAdmin}
      theme={themes.light}
      darkTheme={themes.dark}
      loginPage={Login}
      routerProvider={stableRouterProvider}
    >
      {renderResources}
      {authlessCustomRoutes}
      {distributorPortalRoute}
      {superAdminRoutes}
      {movedNoticeRoute}
    </Admin>
  );
};

const MainAppWithClientVersionCheck = withClientVersionCheck(MainApp);

export default function App() {
  return (
    <TenantBrandingProvider>
      <MainAppWithClientVersionCheck />
    </TenantBrandingProvider>
  );
}
